/**
 * Pure geometry and color helpers for the concentric zones visualization.
 *
 * All functions are pure (no side effects, no imports) so they can be unit-
 * tested without a DOM and shared across all three viz modes (concentric rings,
 * zone strip, floor plan).
 *
 * Design spec reference: section 3.2 "Concentric zones visualization"
 *
 * Ring saturation rules (from spec):
 *   0 devices  - barely visible, almost-transparent cyan (opacity 0.35)
 *   1-2 devices - faint cyan, opacity scales linearly
 *   3-4 devices - full cyan saturation
 *   5+ devices  - amber (suggests coordinated event)
 */
import type { TokenValues } from '@/design/tokens';

// ---------------------------------------------------------------------------
// Ring geometry
// ---------------------------------------------------------------------------

/** Radial proportions for each tier (fraction of half-viewBox). */
const RING_PERCENTS: Record<1 | 2 | 3 | 4, number> = {
  1: 0.45, // perimeter - outer ring
  2: 0.35, // threshold
  3: 0.25, // interior
  4: 0.15, // core - center ring
};

/**
 * Computes the ring radius for a given tier within a square viewBox.
 *
 * @param viewBoxSize - Width (= height) of the SVG viewBox in user units
 * @param tier - Security tier (1 = perimeter/outer, 4 = core/center)
 * @returns Radius in SVG user units
 */
export function getRingRadius(viewBoxSize: number, tier: 1 | 2 | 3 | 4): number {
  const center = viewBoxSize / 2;
  return center * RING_PERCENTS[tier];
}

/**
 * Computes the (x, y) position for a sensor dot orbiting the outer ring.
 *
 * Dots are distributed evenly around the perimeter ring with a small outward
 * offset so they visually ride just outside the ring stroke.
 *
 * @param viewBoxSize - SVG viewBox width/height
 * @param index - Zero-based index of this sensor in the sorted array
 * @param total - Total number of sensors to distribute
 * @param outerRadius - Radius of the outer (perimeter) ring
 * @param dotOffset - Additional radial offset beyond the ring edge (default 8)
 * @returns SVG coordinate pair { x, y }
 */
export function getSensorDotPosition(
  viewBoxSize: number,
  index: number,
  total: number,
  outerRadius: number,
  dotOffset: number = 8,
): { x: number; y: number } {
  // Start at the top (-PI/2) and distribute clockwise
  const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
  const r = outerRadius + dotOffset;
  return {
    x: viewBoxSize / 2 + r * Math.cos(angle),
    y: viewBoxSize / 2 + r * Math.sin(angle),
  };
}

// ---------------------------------------------------------------------------
// Ring saturation color
// ---------------------------------------------------------------------------

/**
 * Returns the stroke color for a concentric ring based on current device count.
 *
 * Color journey (design spec section 3.2):
 *   0        - cyanDim (barely visible baseline)
 *   1-2      - gold (faint; opacity separates these tiers from 3+)
 *   3-4      - gold (full saturation)
 *   5+       - amber (coordinated event warning)
 *
 * Note: opacity is separately driven by getRingOpacity; this function returns
 * only the hue/saturation string.
 *
 * @param deviceCount - Number of detected devices in this ring's tier
 * @param theme - Token values tree (colors.accent.gold / goldDim, colors.status.warn)
 * @returns CSS color string
 */
export function getRingSaturationColor(deviceCount: number, theme: TokenValues): string {
  if (deviceCount === 0) return theme.colors.accent.goldDim;
  if (deviceCount < 5) return theme.colors.accent.gold;
  return theme.colors.status.warn; // amber - coordinated event threshold
}

// ---------------------------------------------------------------------------
// Ring opacity
// ---------------------------------------------------------------------------

/**
 * Returns the stroke opacity for a concentric ring based on current device count.
 *
 * Opacity is monotonically increasing with device count (spec invariant):
 *   getRingOpacity(0) < getRingOpacity(1) < getRingOpacity(3)
 *
 * Values:
 *   0 devices  - 0.35 (barely visible baseline; breathing animation adds 0.25 on top)
 *   1 device   - 0.50
 *   2 devices  - 0.65
 *   3 devices  - 0.80
 *   4 devices  - 0.95
 *   5+ devices - 1.00 (also triggers amber color via getRingSaturationColor)
 *
 * This function is intentionally pure and simple so the monotonicity invariant
 * is trivially verifiable. Unit test asserts: getRingOpacity(1) < getRingOpacity(3).
 *
 * @param deviceCount - Number of detected devices in the ring's tier
 * @returns Opacity value in [0.35, 1.0]
 */
export function getRingOpacity(deviceCount: number): number {
  if (deviceCount === 0) return 0.35;
  return Math.min(1.0, 0.35 + deviceCount * 0.15);
}
